'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { SimplifyJobDetailType } from '@/types/job-detail';
import {
  COMPANY_ROUTER_PATH,
  JOB_DETAIL_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { trackClickEvent } from '@/utils/site-track';
import {
  PREFERRED_DEGREE_BACKGROUND_MAP,
  PREFERRED_WORK_EXPERIENCE_MAP,
} from '@/constants/job-detail';
import {
  PreferredDegreeBackgroundEnum,
  PreferredWorkExperienceEnum,
} from '@/types/job-detail';
import defaultCompanyLogo from '@/assets/company-detail/default-logo.svg';

type Props = {
  data: SimplifyJobDetailType;
  dataEleGroup?: string;
  showContent?: boolean;
};

const JobOpportunityCard = (props: Props) => {
  const {
    data: {
      id,
      name,
      position,
      qualification,
      salary,
      description,
      location,
      education,
      experience,
      company: {
        name: companyName,
        logo: companyLogo,
        id: companyId,
        industry,
      },
    },
    dataEleGroup = 'card_职位',
    showContent,
  } = props;
  const router = useRouter();
  const [companyLogoSrc, setCompanyLogo] = React.useState(
    companyLogo || defaultCompanyLogo,
  );

  const url = `${JOB_DETAIL_PAGE_ROUTER_PATH}${id}/`;

  return (
    <div
      className="rounded-lg border border-white-smoke shadow-3xl cursor-pointer overflow-hidden group flex flex-col hover:border-web-blue-500"
      data-observer-id={id}
      data-ele-id={`${id}_${name}`}
      data-ele-group={dataEleGroup}
      onClick={(e) => {
        trackClickEvent(e);
        router.push(url);
      }}
    >
      <div className="px-6 pt-5 pb-4">
        <p className="flex items-center justify-between gap-1">
          <Link
            className="font-semibold line-clamp-1 max-w-[80%] group-hover:text-web-blue-500"
            title={name}
            href={url}
            onClick={(e) => trackClickEvent(e, true)}
          >
            {name}
          </Link>
          <span
            className="text-web-orange-600 line-clamp-1 font-semibold"
            title={salary}
          >
            {salary}
          </span>
        </p>
        {(location || education || experience) && (
          <div className="flex mt-1.5 gap-3">
            {[
              (typeof location === 'string'
                ? location
                : location?.name
              )?.replace('市', ''),
              PREFERRED_DEGREE_BACKGROUND_MAP[education] &&
                (education === PreferredDegreeBackgroundEnum.None
                  ? '学历'
                  : '') + PREFERRED_DEGREE_BACKGROUND_MAP[education],
              PREFERRED_WORK_EXPERIENCE_MAP[experience] &&
                (experience === PreferredWorkExperienceEnum.None
                  ? '经验'
                  : '') + PREFERRED_WORK_EXPERIENCE_MAP[experience],
            ]
              .filter(Boolean)
              .map((item, index) => (
                <span
                  key={index}
                  className="text-xs px-2 py-1 rounded-lg bg-web-grey-350 text-web-grey-600"
                >
                  {item}
                </span>
              ))}
          </div>
        )}
      </div>
      {showContent ? (
        <p
          className="px-5 mb-5 text-xs h-8 line-clamp-2 break-all text-web-grey-600 mt-auto"
          dangerouslySetInnerHTML={{
            __html: (position || qualification || description || '')
              .slice(0, 40)
              .replace(/(\n|\r|\r\n)/g, '<br/>'),
          }}
        />
      ) : (
        <div
          className="flex items-center justify-between gap-1 px-6 py-2.5 text-sm text-web-grey-600 mt-auto"
          style={{
            background: 'linear-gradient(90deg,#e8f3fc 0%,#fbfcff 100%)',
          }}
        >
          <div className="flex items-center gap-1">
            <div className="relative w-4 h-4 rounded-md overflow-hidden shrink-0">
              <Image
                src={companyLogoSrc}
                alt={`${companyName}-logo`}
                fill
                style={{
                  objectFit: 'contain',
                }}
                onError={() => setCompanyLogo(defaultCompanyLogo)}
              />
            </div>
            <Link
              className="line-clamp-1 hover:underline"
              title={companyName}
              href={`${COMPANY_ROUTER_PATH}${companyId}/`}
              onClick={(e) => trackClickEvent(e, true)}
            >
              {companyName}
            </Link>
          </div>
          <span className="line-clamp-1">{industry?.name || ''}</span>
        </div>
      )}
    </div>
  );
};

export default JobOpportunityCard;
